"use client";

import { cn } from "../lib/utils";
import { ICONOIR_ICON_NAMES, type IconoirIconName } from "../lib/iconoirIcons";

interface IconPickerProps {
  label?: string;
  /** Currently selected icon name, or null for "no icon". */
  value: string | null;
  onChange: (value: IconoirIconName | null) => void;
  /** Icon names to offer; defaults to the curated list. */
  icons?: readonly IconoirIconName[];
  errortext?: string;
}

/** "health-shield" -> "Health shield" for the selection caption. */
function humanizeIconName(name: string): string {
  const words = name.replace(/-/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Grid of curated Iconoir icons, each rendered as a live preview of itself.
 * Selecting the active icon clears the selection.
 */
export function IconPicker({
  label,
  value,
  onChange,
  icons = ICONOIR_ICON_NAMES,
  errortext,
}: IconPickerProps) {
  return (
    <div>
      {label && (
        <label className="mb-1 block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <div
        role="radiogroup"
        aria-label={label ?? "Icon"}
        className="grid max-h-44 grid-cols-8 gap-1 overflow-y-auto rounded-md bg-white p-2 shadow-sm ring-1 ring-inset ring-gray-300"
      >
        {icons.map((icon) => {
          const selected = value === icon;
          return (
            <button
              key={icon}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={icon}
              title={icon}
              onClick={() => onChange(selected ? null : icon)}
              className={cn(
                "flex h-9 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100",
                selected && "bg-blue-500/10 text-blue-500 ring-1 ring-blue-500",
              )}
            >
              <span className={cn(`iconoir-${icon}`, "text-xl")} />
            </button>
          );
        })}
      </div>
      {errortext ? (
        <p className="mt-1 text-xs text-error">{errortext}</p>
      ) : (
        <p className="mt-1 text-xs text-gray-500">
          {value ? `Selected: ${humanizeIconName(value)}` : "No icon selected"}
        </p>
      )}
    </div>
  );
}
